import { useState, useEffect, useRef } from 'react';
import { collection, addDoc, deleteDoc, doc, onSnapshot, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase/firestore';
import { uploadPhoto, deletePhoto } from '../firebase/storage';
import { useAuth } from '../context/AuthContext';
import { displayName } from '../utils/displayName';
import DateRangePicker from './DateRangePicker';
import TripBook from './TripBook';
import './TripTimeline.scss';

const EMPTY_FORM = { title: '', destination: '', startDate: '', endDate: '', description: '' };

const TripTimeline = () => {
  const { user } = useAuth();
  const [trips, setTrips]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showForm, setShowForm]     = useState(false);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [coverFile, setCoverFile]   = useState(null);
  const [saving, setSaving]         = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [openTrip, setOpenTrip]     = useState(null);   // trip being viewed in book
  const fileRef = useRef(null);

  useEffect(() => {
    const q = query(collection(db, 'trips'), orderBy('startDate', 'desc'));
    const unsub = onSnapshot(q, snap => {
      setTrips(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, []);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleDateChange = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      let coverPhotoUrl = null;
      let coverPhotoPath = null;
      if (coverFile) {
        const result = await uploadPhoto(coverFile, 'trips');
        coverPhotoUrl = result.url;
        coverPhotoPath = result.path;
      }
      await addDoc(collection(db, 'trips'), {
        ...form,
        coverPhotoUrl,
        coverPhotoPath,
        addedBy: user.email,
        createdAt: serverTimestamp(),
      });
      setForm(EMPTY_FORM);
      setCoverFile(null);
      setShowForm(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (trip) => {
    if (trip.coverPhotoPath) await deletePhoto(trip.coverPhotoPath);
    await deleteDoc(doc(db, 'trips', trip.id));
    setDeleteTarget(null);
  };

  const formatDate = (str) => {
    if (!str) return '';
    return new Date(str + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', year: 'numeric', day: 'numeric' });
  };

  return (
    <div className="trip-timeline">
      <div className="timeline-header">
        <div>
          <h2>Our Journey</h2>
          <p className="timeline-subtitle">{trips.length} {trips.length === 1 ? 'trip' : 'trips'} together</p>
        </div>
        <button className="btn-add-trip" onClick={() => setShowForm(true)}>+ Add Trip</button>
      </div>

      {loading ? (
        <div className="timeline-loading">Loading trips...</div>
      ) : trips.length === 0 ? (
        <div className="timeline-empty">
          <p>No trips logged yet.</p>
          <p>Add your first adventure!</p>
        </div>
      ) : (
        <div className="timeline">
          {trips.map((trip, idx) => (
            <div key={trip.id} className={`timeline-item ${idx % 2 === 0 ? 'left' : 'right'}`}>
              <div className="timeline-dot" />
              <div className="timeline-card" onClick={() => setOpenTrip(trip)}>
                {trip.coverPhotoUrl && (
                  <img className="trip-cover" src={trip.coverPhotoUrl} alt={trip.title} />
                )}
                <div className="trip-content">
                  <div className="trip-meta">
                    <span className="trip-destination">📍 {trip.destination}</span>
                    <span className="trip-dates">
                      {formatDate(trip.startDate)}
                      {trip.endDate && trip.endDate !== trip.startDate && ` – ${formatDate(trip.endDate)}`}
                    </span>
                  </div>
                  <h3 className="trip-title">{trip.title}</h3>
                  {trip.description && <p className="trip-description">{trip.description}</p>}
                  <div className="trip-footer">
                    <span className="trip-added-by">by {displayName(trip.addedBy)}</span>
                    <div className="trip-footer-actions">
                      <span className="trip-open-hint">✦ Open journal</span>
                      <button
                        className="btn-delete-trip"
                        onClick={e => { e.stopPropagation(); setDeleteTarget(trip); }}
                      >Delete</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <div className="timeline-line" />
        </div>
      )}

      {/* Add trip modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>New Trip</h3>
              <button className="modal-close" onClick={() => setShowForm(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="trip-form">
              <input name="title" placeholder="Trip title *" value={form.title} onChange={handleChange} required />
              <input name="destination" placeholder="Destination *" value={form.destination} onChange={handleChange} required />

              <DateRangePicker
                startDate={form.startDate}
                endDate={form.endDate}
                onChange={handleDateChange}
              />

              <textarea name="description" placeholder="Notes about the trip..." value={form.description} onChange={handleChange} rows={3} />
              <div className="cover-upload">
                <button type="button" className="btn-cover" onClick={() => fileRef.current?.click()}>
                  {coverFile ? `✓ ${coverFile.name}` : '+ Cover Photo (optional)'}
                </button>
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={e => setCoverFile(e.target.files[0] ?? null)} />
              </div>
              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn-save" disabled={saving || !form.startDate}>{saving ? 'Saving...' : 'Save Trip'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div className="modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="confirm-box" onClick={e => e.stopPropagation()}>
            <p>Delete "{deleteTarget.title}"?</p>
            <div className="confirm-actions">
              <button className="btn-confirm-cancel" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="btn-confirm-delete" onClick={() => handleDelete(deleteTarget)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Trip book */}
      {openTrip && (
        <TripBook trip={openTrip} onClose={() => setOpenTrip(null)} />
      )}
    </div>
  );
};

export default TripTimeline;
